import { useState } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy, Send, X, CheckCircle2, Lock, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const CATEGORIES = ["dispatch", "batching", "invoicing", "gps", "login", "general"];
const URGENCY = [
  { v: "low", label: "Low", cls: "border-industrial text-muted-industrial" },
  { v: "normal", label: "Normal", cls: "border-sky-500 text-sky-400" },
  { v: "high", label: "High", cls: "border-amber-500 text-amber-400" },
  { v: "critical", label: "CRITICAL · Plant stopped", cls: "border-red-500 text-red-400" },
];

export default function SosWidget({ locked = false }) {
  const loc = useLocation();
  const { org } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("general");
  const [urgency, setUrgency] = useState("normal");
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const includedIn = org?.feature_tiers?.sos_emergency;
  const currentTier = org?.tier_name;

  const submit = async () => {
    if (!message.trim()) return toast.error("Describe the problem first");
    setSending(true);
    try {
      const { data } = await api.post("/support/sos", { category, urgency, message, page: loc.pathname, callback_phone: phone || null });
      setSent(data);
      setMessage("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="sos-fab"
        onClick={() => { setOpen((v) => !v); setSent(null); }}
        title={locked ? "SOS Emergency — upgrade to unlock" : "24/7 SOS Support"}
        className="fixed bottom-5 right-5 z-[60] inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-500 text-white px-4 py-2.5 shadow-[0_8px_30px_rgba(220,38,38,0.45)] transition-transform hover:-translate-y-0.5"
      >
        <LifeBuoy className="w-4 h-4" />
        <span className="mono text-[11px] font-bold uppercase tracking-widest">SOS</span>
      </button>

      {open && locked && (
        <div data-testid="sos-locked-panel" className="fixed bottom-20 right-5 z-[60] w-[360px] max-w-[calc(100vw-2.5rem)] bg-surface border border-industrial shadow-2xl rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-red-600 text-white">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest opacity-80">ArmorBatch Global</div>
              <div className="heading font-black uppercase text-sm">SOS Emergency</div>
            </div>
            <button type="button" data-testid="sos-close" onClick={() => setOpen(false)} className="p-1 hover:bg-white/15 rounded"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5 space-y-4 text-center">
            <div className="w-12 h-12 rounded-full bg-primary-orange/15 flex items-center justify-center mx-auto">
              <Lock className="w-5 h-5 text-primary-orange" />
            </div>
            <div>
              <div className="heading text-base font-black">SOS Emergency is locked</div>
              <p className="text-sm text-muted-industrial mt-1">
                {currentTier ? `Your ${currentTier} plan doesn't include 24/7 SOS Emergency support.` : "This feature isn't included in your current plan."}
                {includedIn ? ` It's available on the ${includedIn} plan and above.` : ""}
              </p>
            </div>
            <div className="border border-primary-orange/40 bg-primary-orange/5 p-3 text-left flex items-center gap-2 text-primary-orange text-xs uppercase tracking-widest font-bold">
              <Sparkles className="w-3.5 h-3.5" /> Upgrade to unlock priority SOS
            </div>
            <a href="mailto:admin@armorbatch.com?subject=ArmorBatch%20—%20Upgrade%20to%20unlock%20SOS"
              data-testid="sos-upgrade-contact"
              className="block w-full bg-primary-orange text-black font-bold py-3 uppercase tracking-widest text-sm hover:opacity-90">
              Contact us to upgrade
            </a>
          </div>
        </div>
      )}

      {open && !locked && (
        <div data-testid="sos-panel" className="fixed bottom-20 right-5 z-[60] w-[360px] max-w-[calc(100vw-2.5rem)] bg-surface border border-industrial shadow-2xl rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-red-600 text-white">
            <div>
              <div className="mono text-[10px] uppercase tracking-widest opacity-80">ArmorBatch Global</div>
              <div className="heading font-black uppercase text-sm">24/7 SOS Support</div>
            </div>
            <button type="button" data-testid="sos-close" onClick={() => setOpen(false)} className="p-1 hover:bg-white/15 rounded"><X className="w-4 h-4" /></button>
          </div>

          {sent ? (
            <div className="p-5 space-y-3" data-testid="sos-sent">
              <div className="flex items-center gap-2 text-emerald-500"><CheckCircle2 className="w-5 h-5" /><span className="font-bold">Ticket {sent.id} raised</span></div>
              <p className="text-sm text-muted-industrial">
                Priority support has been alerted{sent.email_status === "sent" ? " by email" : ""}. A specialist will contact you
                {sent.urgency === "critical" ? " immediately" : " shortly"}.
              </p>
              <Button data-testid="sos-another" variant="outline" className="rounded-none w-full" onClick={() => setSent(null)}>Raise another</Button>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              <div>
                <div className="label-tag mb-1">Area</div>
                <div className="flex flex-wrap gap-1">
                  {CATEGORIES.map((c) => (
                    <button key={c} type="button" data-testid={`sos-cat-${c}`} onClick={() => setCategory(c)}
                      className={`mono text-[10px] uppercase tracking-wider px-2 py-1 border ${category === c ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="label-tag mb-1">Urgency</div>
                <div className="grid grid-cols-2 gap-1">
                  {URGENCY.map((u) => (
                    <button key={u.v} type="button" data-testid={`sos-urgency-${u.v}`} onClick={() => setUrgency(u.v)}
                      className={`mono text-[10px] uppercase tracking-wider px-2 py-1.5 border ${u.cls} ${urgency === u.v ? "bg-white/5 ring-1 ring-current" : "opacity-70"}`}>
                      {u.label}
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                data-testid="sos-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                placeholder="What's happening? Include truck / docket / customer if relevant."
                className="w-full bg-transparent border border-industrial p-2 text-sm text-ink placeholder:text-muted-industrial/60 focus:outline-none focus:border-primary-orange"
              />
              <Input data-testid="sos-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Callback phone (optional)" className="rounded-none bg-transparent border-industrial text-sm" />
              <Button data-testid="sos-submit" onClick={submit} disabled={sending} className="w-full rounded-none bg-red-600 hover:bg-red-500 text-white">
                <Send className="w-4 h-4 mr-2" /> {sending ? "Sending…" : "Send SOS"}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
